import React from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, map, pick } from 'lodash';
import { compose, branch, renderComponent } from 'recompose';
import withCookies from 'react-cookie/lib/withCookies';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';

import { PageTypes } from '@sparefoot/segment-react';
import { withShowHide } from '@sparefoot/react-core/decorators';
import { mapDispatchToActions } from '@sparefoot/react-core/utils';
import { SegmentRegion, withSegmentEcommerceContext } from '@sparefoot/react-core/segment';

import { phonePropType, metaPropTypes } from 'config/propTypes';
import { withSearchHandler, withErrorHandler } from 'decorators';
import { withGTMPageViewEvent } from 'decorators/gtm';
import { withSearchPageHandlers } from 'decorators/search';
import {
	Page, Section, Container, Group, Row, Column
} from 'components/core/Page';
import { H1, H2 } from 'components/core/Typography';
import { Button } from 'components/core/Button';
import { Image } from 'components/core/Image';
import { Notification } from 'components/core/Notification';
import { SearchBreadcrumbs } from 'components/search/SearchBreadcrumbs';
import { SearchResults } from 'components/search/SearchResults';
import { Card } from 'components/legacy/Card';
import { LoadingPage } from 'components/core/LoadingPage';
import { LoadingMask } from 'components/core/LoadingMask';
import { LazyHydrate } from 'components/core/LazyHydrate';
import { InformedSearchHeader } from 'components/search/InformedSearch';
import { SearchBox } from 'components/search/SearchBox';
import { SearchContent } from 'components/search/SearchContent';
import { SidebarFilters } from 'components/search/SearchFilters';
import { SearchFiltersModal } from 'components/search/SearchFiltersModal';
import { SearchMap } from 'components/search/SearchMap';
import { SearchPagination } from 'components/search/SearchPagination';
import { PaginationSummary } from 'components/search/PaginationSummary';
import { RecentReservations } from 'components/search/RecentReservations';
import { ExpressCheckInBanner } from 'components/search/ExpressCheckInBanner';
import { StorageStats } from 'components/search/StorageStats';
import { NearbyCitiesComparison } from 'components/search/NearbyCitiesComparison';
import Sliders from 'components/svg/Sliders';
import Expand from 'components/svg/Expand';
import { ScarcityBanner } from 'components/search/ScarcityBanner';
import { SearchTray } from 'components/search/SearchTray';
import { FilterSearchHero } from 'components/search/FilterSearchHero';

import searchSelector from 'store/selectors/search';
import {
	getSearchResults,
	showSearchFilters,
	hideSearchFilters,
	updateSearchFilters
} from 'store/modules/search';
import { getLocalCityPhoneNumber } from 'store/modules/phone';

import './SearchPage.scss';

export const enhance = compose(
	withCookies,
	connect(
		searchSelector,
		{
			getSearchResults,
			hideSearchFilters,
			showSearchFilters,
			updateSearchFilters,
			getLocalCityPhoneNumber
		},
		mapDispatchToActions
	),
	withErrorHandler,
	branch((props) => props.loading && !props.searchId, renderComponent(LoadingPage)),
	withSearchHandler,
	withSearchPageHandlers,
	withShowHide('map'),
	withShowHide('searchFilterModal'),
	withSegmentEcommerceContext((props) => ({
		listId: `search results: ${get(props, 'searchLocation.city')}, ${get(props, 'searchLocation.fullState')}`,
		category: props.searchLocationText,
		label: 'enhancedecommerce',
		page: props.page,
		searchId: props.searchId,
		searchTerm: props.searchLocationText
	})),
	withGTMPageViewEvent(PageTypes.SEARCH, ({
		facilities, page, searchContent, searchId, searchLocation, searchParams
	}) => ({
		search_result: {
			id: searchId,
			listing_ids: map(facilities, 'id'),
			query: searchParams.location,
			page
		},
		location: pick(searchLocation, ['id', 'city', 'state']),
		site_content: {
			id: searchContent.contentId
		},
		page_subtype: searchParams.pageType
	})),
);

export function SearchPage({
	actions,
	baseUrl,
	cloudfrontUrl,
	cityUrl,
	errorMessage,
	facetCounts,
	facilities,
	facilityNotFound,
	filtersShowing,
	handleSearch,
	headline,
	subtitle,
	loading,
	meta,
	mapShowing,
	searchFilterModalShowing,
	onShowSearchFilterModal,
	onHideSearchFilterModal,
	onSearch,
	onHideMap,
	onShowMap,
	page,
	pageType,
	phone,
	recentReservations,
	searchContent,
	searchDividerIndex,
	visitId,
	searchId,
	searchLocation,
	searchLocationText,
	searchParams,
	searchType,
	showInformedSearch,
	showRecommendedUnit,
	stateUrl,
	staticMapUrl,
	storageTypeUrls,
	totalListings,
	totalPages,
	trackingPixel,
	faqContent,
	faqShowing,
	withinCallCenterHours,
	stickyTrayPhoneNumber
}) {
	return (
		<Page
			className="search-page"
			trackingPixel={trackingPixel}
			loading={loading}
			pageType={pageType}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
			noIndex={meta.noindex}
			prev={meta.prev}
			next={meta.next}
		>
			{(pageType === 'cityLandingPage' ||
			pageType === 'amenityLandingPage' ||
			pageType === 'vehicleLandingPage') && (
				<Helmet>
					<script type="application/ld+json">
						{`
							{
							"@context": "http://schema.org",
							"@type": "WebSite",
							"name": "SelfStorage.com",
							"url": "https://www.selfstorage.com/",
							"potentialAction": {
								"@type": "SearchAction",
								"target": "https://www.selfstorage.com/search?location={search_term_string}",
								"query-input": "required name=search_term_string"
							}
							}
						`}
					</script>
				</Helmet>
			)}
			{
				// IMPORTANT: It is usually recommended to render or not components based on flags
				// but in this case rendering issues related to dates and timezones generated issues.
				// This may be considered as an exception to the rule.
				// See https://sparefoot.atlassian.net/browse/RED-2096 for reference
				<SearchTray
					delay={10000}
					visitId={visitId}
					searchId={searchId}
					searchLocation={searchLocation}
					stickyTrayPhoneNumber={stickyTrayPhoneNumber}
					onShowSearchFilterModal={onShowSearchFilterModal}
					getLocalCityPhoneNumber={actions.getLocalCityPhoneNumber}
					invisible={!withinCallCenterHours} // Always render but hide when off call center hours
				/>
			}

			{!errorMessage && !showInformedSearch &&
				// on non-CLPs, don't show this experiment at all.
				<ScarcityBanner backgroundColor="red" />
			}

			{!errorMessage && showInformedSearch && (
				<ScarcityBanner backgroundColor="priceBlue" />
			)}

			<Container gutters="xlarge">
				<SearchBreadcrumbs
					className="mobile-breadcrumbs"
					location={searchLocation}
					cityUrl={cityUrl}
					stateUrl={stateUrl}
					searchType={searchType}
				/>
			</Container>

			{showInformedSearch && (
				<FilterSearchHero
					headline={headline}
					subtitle={subtitle}
					totalListings={totalListings}
					searchLocation={searchLocation}
				/>
			)}
			{searchParams.informedSearch &&
				<InformedSearchHeader
					location={searchLocation}
					className="mobile"
				/>
			}
			<Section className="city-banner">
				<Container>
					{!showInformedSearch && !searchParams.informedSearch &&
						<Group className="headlines">
							<H1
								last
								size="medium"
								className="headline"
							>
								{headline}
							</H1>
							<H2 className="subheading">
								{(subtitle) || 'Find The Storage Unit You Need At The Price You Want'}
							</H2>
						</Group>
					}

					<div className="banner-controls">
						{!showInformedSearch && !searchParams.informedSearch &&
							<SegmentRegion segmentCategory="search-interactions">
								<Group>
									<SearchBox
										location={searchLocationText}
										onSearch={handleSearch}
										id="header-search"
										useBrowserLocation
										autoSearch
									/>
								</Group>
							</SegmentRegion>
						}
						<SegmentRegion segmentCategory="sticky filter interactions">
							<Button
								className="mobile-filters-button"
								segmentLabel="filter by amenity"
								onClick={actions.showSearchFilters}
								secondary
								fullWidth
							>
								<Sliders /> View all filters
							</Button>
						</SegmentRegion>
					</div>
				</Container>
			</Section>
			<Container>
				<div className="search-summary">
					{totalPages > 0 &&
						<PaginationSummary
							page={page}
							totalPages={totalPages}
							totalItems={totalListings}
						/>
					}

					<SearchBreadcrumbs
						className="summary-breadcrumbs"
						location={searchLocation}
						cityUrl={cityUrl}
						stateUrl={stateUrl}
						searchType={searchType}
					/>
				</div>
			</Container>
			<LazyHydrate id="lazy-search-page" noLazy={!showInformedSearch}>
				<Section id="search-page-results">
					<Container>
						<Row reverse>
							<Column xlarge={9}>
								{searchParams.informedSearch &&
									<InformedSearchHeader
										className="desktop"
										location={searchLocation}
									/>
								}
								{totalListings > 0 &&
									<RecentReservations
										reservations={recentReservations}
										location={searchLocation}
									/>
								}
								<SegmentRegion segmentCategory="omi-banner">
									<ExpressCheckInBanner
										searchParams={searchParams}
										onFilter={onSearch}
									/>
								</SegmentRegion>
								<SearchResults
									baseUrl={baseUrl}
									cloudfrontUrl={cloudfrontUrl}
									error={errorMessage}
									facilities={facilities}
									loading={loading}
									location={searchLocation}
									isCLP={pageType === 'cityLandingPage' && showInformedSearch}
									searchType={searchType}
									searchDividerIndex={searchDividerIndex}
									// should only show recommended units if the modal is not showing
									showRecommendedUnit={!searchFilterModalShowing && showRecommendedUnit}
									phone={phone}
									onSearch={handleSearch}
									searchId={searchId}
									segmentCategory="facility interactions"
								/>
								<SearchPagination
									numPages={totalPages}
									searchParams={searchParams}
									selectedPage={page}
								/>
								<SearchContent
									pageType={pageType}
									location={searchLocation}
									content={searchContent}
									searchParams={searchParams}
									faqContent={faqContent}
									faqShowing={faqShowing}
								/>
							</Column>
							<Column xlarge={3} className="sidebar">
								<Group className="sidebar-search-box">
									<SegmentRegion segmentCategory="search-interactions">
										<SearchBox
											location={searchLocationText}
											onSearch={handleSearch}
											id="sidebar-search"
											useBrowserLocation
											autoSearch
										/>
									</SegmentRegion>
								</Group>
								{staticMapUrl &&
									<Group className="static-map">
										<Card segmentCategory="map interactions">
											<Button
												segmentLabel="map image widget"
												className="open-map-button"
												onClick={onShowMap}
											>
												<Image
													src={staticMapUrl}
													className="static-map-image"
													alt={`Map of ${searchLocation.city}`}
													lazyLoad
												/>
												<span className="open-icon">
													Open Map <Expand />
												</span>
											</Button>
										</Card>
									</Group>
								}
								{!!searchType &&
									<Group>
										<SidebarFilters
											facetCounts={facetCounts}
											showing={filtersShowing}
											onClose={actions.hideSearchFilters}
											onFilter={onSearch}
											searchParams={searchParams}
											searchType={searchType}
											storageTypeUrls={storageTypeUrls}
										/>
									</Group>
								}
								{!isEmpty(get(searchContent, 'nearbyCities')) &&
									<Group>
										<NearbyCitiesComparison
											nearbyCities={searchContent.nearbyCities}
											searchCity={searchContent.pricingStats}
											location={searchLocation}
										/>
									</Group>
								}
								{(!isEmpty(get(searchContent, 'pricingStats.lowestPrices')) ||
									!isEmpty(get(searchContent, 'pricingStats.averagePrices'))) &&
									<Group>
										<StorageStats
											city={searchLocation.city}
											facilityCount={totalListings}
											pricingStats={searchContent.pricingStats}
										/>
									</Group>
								}
							</Column>
						</Row>
					</Container>
				</Section>
				{mapShowing &&
					<SearchMap
						facilities={facilities}
						loading={loading}
						location={searchLocation}
						onClose={onHideMap}
						phone={phone}
						searchType={searchType}
						searchDividerIndex={searchDividerIndex}
						showRecommendedUnit={showRecommendedUnit}
						onSearch={onSearch}
					/>
				}
			</LazyHydrate>
			<SearchFiltersModal
				title="Filter your search"
				segmentLabel="search filters tray modal"
				showing={searchFilterModalShowing}
				onClose={onHideSearchFilterModal}
				facetCounts={facetCounts}
				onFilter={onSearch}
				searchParams={searchParams}
				searchType={searchType}
				storageTypeUrls={storageTypeUrls}
			/>
			{facilityNotFound &&
				<Notification>
					The facility you were looking for was not found.
					Try these nearby facilities.
				</Notification>
			}
			{!!loading &&
				<LoadingMask />
			}
		</Page>
	);
}

SearchPage.propTypes = {
	actions: PropTypes.shape({
		hideSearchFilters: PropTypes.func.isRequired,
		showSearchFilters: PropTypes.func.isRequired
	}).isRequired,
	baseUrl: PropTypes.string,
	cloudfrontUrl: PropTypes.string,
	cityUrl: PropTypes.string,
	errorMessage: PropTypes.string,
	facetCounts: PropTypes.object.isRequired,
	facilities: PropTypes.arrayOf(PropTypes.object),
	facilityNotFound: PropTypes.bool,
	filtersShowing: PropTypes.bool.isRequired,
	handleSearch: PropTypes.func.isRequired,
	headline: PropTypes.string.isRequired,
	subtitle: PropTypes.string,
	loading: PropTypes.bool,
	meta: PropTypes.shape(metaPropTypes),
	mapShowing: PropTypes.bool,
	searchFilterModalShowing: PropTypes.bool,
	onShowSearchFilterModal: PropTypes.func,
	onHideSearchFilterModal: PropTypes.func,
	onSearch: PropTypes.func.isRequired,
	onHideMap: PropTypes.func.isRequired,
	onShowMap: PropTypes.func.isRequired,
	page: PropTypes.number,
	pageType: PropTypes.string,
	phone: phonePropType,
	recentReservations: PropTypes.number,
	searchContent: PropTypes.object,
	searchDividerIndex: PropTypes.number,
	searchId: PropTypes.string,
	searchLocation: PropTypes.object,
	searchLocationText: PropTypes.string,
	searchParams: PropTypes.object,
	searchType: PropTypes.string,
	showInformedSearch: PropTypes.bool,
	showRecommendedUnit: PropTypes.bool,
	stateUrl: PropTypes.string,
	staticMapUrl: PropTypes.string,
	storageTypeUrls: PropTypes.shape({
		storage: PropTypes.string,
		vehicle: PropTypes.string
	}).isRequired,
	totalListings: PropTypes.number,
	totalPages: PropTypes.number,
	trackingPixel: PropTypes.string.isRequired,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	withinCallCenterHours: PropTypes.bool,
	visitId: PropTypes.string,
	stickyTrayPhoneNumber: PropTypes.object
};

export default enhance(SearchPage);
